import {ChartApi, ChartToolsApi} from "../../components/chartiq/components/chart/ChartApi";
import {
    ChartIQDrawingTool,
    ChartIQSerializedDrawings,
    ChartIQVectorParameter,
    ChartIQVectorParameterValue
} from "../../components/chartiq/types/ChartIQ";
import {Optional} from "../../js/util/Optional";
import {StringKeyed} from "../../types/GenericTypes";
import {Subscription} from "rxjs/Subscription";
import {Subject} from "rxjs/Subject";
import {Observable} from "rxjs/Observable";

/** A wrapper for the ChartAPI which will forward certain calls onto all APIs */
export class ChartApiWrapper implements ChartToolsApi {

    private drawingStack: Array<ChartApi> = [];
    private drawingSubs: StringKeyed<Subscription> = {};
    private drawingChanges$ = new Subject<ChartIQSerializedDrawings>();

    public constructor(private apis: Array<ChartApi> = []) {

    }

    public add(api: ChartApi) {
        const existing = this.get(api.id);
        existing.ifEmpty(() => {
            this.apis.push(api);
            api.onDrawingsChanged(() => {
                this.drawingStack.push(api);
            });
            const sub = api.drawingChanges.subscribe(dr => {
                this.drawingChanges$.next(dr)
            });
            this.drawingSubs[api.id] = sub;
        });
    }

    public remove(id: string) {
        const api = this.apis.find(a => a.id === id);
        if (api != null) {
            this.apis = this.apis.filter(a => a.id !== id);
            this.drawingStack = this.drawingStack.filter(a => a.id !== id);
            const sub = this.drawingSubs[id];
            sub.unsubscribe();
        }
    }

    public reset() {
        this.apis = [];
        this.drawingStack = [];
    }

    public get(id: string): Optional<ChartApi> {
        const api = this.apis.find(a => a.id === id);
        return Optional.of(api);
    }

    public get id(): string {
        throw Error("Unsupported Operation");
    }

    public clearDrawings(): void {
        this.apis.forEach(api => api.clearDrawings());
    }

    public removeAllStudies(): void {
        this.apis.forEach(api => api.removeAllStudies());
    }

    public setTool(tool: ChartIQDrawingTool | null): void {
        this.apis.forEach(api => api.setTool(tool));
    }

    public setToolParameter(param: ChartIQVectorParameter, value: ChartIQVectorParameterValue | null): void {
        this.apis.forEach(api => api.setToolParameter(param, value));
    }

    // Since we need this for the DrawingFormat, we have to just assume all charts are the same
    public getToolParameter(param: ChartIQVectorParameter): ChartIQVectorParameterValue | null {
        return Optional.of(this.apis[0])
                       .map(api => api.getToolParameter(param))
                       .orNull();
    }

    // Since we need this for the DrawingFormat, we have to just assume all charts are the same
    public defaultColour(): string | null {
        return Optional.of(this.apis[0])
                       .map(api => api.defaultColour())
                       .orNull();
    }

    public undo(): void {
        Optional.of(this.drawingStack.pop())
                .ifPresent(api => api.undo());
    }

    public canUndo(): boolean {
        return this.drawingStack.length > 0;
    }

    public get drawingChanges(): Observable<ChartIQSerializedDrawings> {
        return this.drawingChanges$.asObservable();
    }

}
